import { useContext, useEffect, useState } from "react";
import { KanbanBoardContainer, KanbanBoard } from "./KanbanBoardContainer";
import { KanbanCardMemo } from "./KanbanCard";
import KanbanColumn from "./KanbanColumn";
import KanbanItem from "./KanbanItem";
import { KanbanColumnSkeleton, KanbanCardSkeleton } from "./KanbanSkeleton";
import { TaskPopup } from "../Popups/TaskPopup";
import Delete from "../../Helpers/Delete/DeletePopup";
import {
	useEditTaskMutation,
	useGetTasksByFilterQuery,
} from "../../../Redux/service/Tasks/Tasks";
import { popupcontext } from "../../../context/Popupscontext";
import { filtrationcontext } from "../../../context/Filtercontext";
import { toast } from "react-toastify";
import trans from "../../../utils";
import { useSelector } from "react-redux";
import { useTechnicians } from "../../../hooks/useTechnicians";
import { Tag } from "antd";
import { FaUser } from "react-icons/fa6";
import usePermission from "../../../hooks/usePermission";

const columns = [
	{ id: "unAssigned", title: "Unassigned" },
	{ id: "todo", title: "TODO" },
	{ id: "inProgress", title: "In Progress" },
	{ id: "done", title: "Done" },
	// { id: "delayed", title: "Delayed" },
];

const TasksBoardGroupByFieldStaff = ({
	fromDate,
	toDate,
	setRefresh,
	refresh,
	setRefreshing,
	viewMode,
	selectedPOIs,
	showBy,
	selectedStaff,
}) => {
	const { handleShow, edited, setEdited, setDeleteItem, deleteItem } =
		useContext(popupcontext);
	const { searchQuery, setSearchQuery } = useContext(filtrationcontext);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [selectedTask, setSelectedTask] = useState("");
	const [deleteTask, setDeleteTask] = useState("");
	const { technicians: users } = useTechnicians();
	const [rescheduleTask, setRescheduleTask] = useState(null);
	const [editTask] = useEditTaskMutation();
	const Livetasks = useSelector((state) => state.webSocket.tasks);
	const colors = [
		"blue",
		"green",
		"red",
		"orange",
		"purple",
		"magenta",
		"cyan",
	];

	const { edit, delete: canDelete } = usePermission("FIELD_TASKS");

	const {
		data: tasksData,
		isFetching,
		refetch,
	} = useGetTasksByFilterQuery(
		{
			limit: 10,
			page: 1,
			search: searchQuery,
			showBy: showBy,
			poiId: showBy === "poi" ? selectedPOIs : null,
			assignedToId: showBy === "fieldStaff" ? selectedStaff : null,
			fromDate: fromDate,
			toDate: toDate,
		},
		{ refetchOnMountOrArgChange: true }
	);

	const [tasks, setTasks] = useState([]);

	// Group tasks by field staff (assignedTo)
	const tasksByFieldStaff = tasks.reduce((acc, task) => {
		const fieldStaffId = task.assignedTo?.id || "unassigned";
		if (!acc[fieldStaffId])
			acc[fieldStaffId] = { assignedTo: task.assignedTo, tasks: [] };
		acc[fieldStaffId].tasks.push(task);
		return acc;
	}, {});

	useEffect(() => {
		setRefreshing?.(isFetching);
	}, [isFetching, setRefreshing]);

	useEffect(() => {
		if (tasksData?.data) {
			setTasks(tasksData?.data || []);
		}
	}, [tasksData]);

	useEffect(() => {
		async function fetchdata() {
			if (edited) {
				await refetch();
				setEdited(false);
			}
			if (refresh) {
				await refetch();
				setRefresh(false);
			}
			if (Livetasks) {
				await refetch();
			}
		}
		fetchdata();
	}, [edited, refresh, Livetasks]);

	useEffect(() => {
		return () => {
			setSearchQuery("");
		};
	}, [setSearchQuery]);

	useEffect(() => {
		async function fetchDataAfterDelete() {
			if (deleteItem.condition === true && deleteItem.value === "task") {
				await refetch();
				setDeleteItem({ condition: null, value: null });
			}
		}
		fetchDataAfterDelete();
	}, [deleteItem.condition, deleteItem.value, refetch, setDeleteItem]);

	if (isFetching || !Array.isArray(tasks)) return <PageSkeleton />;

	const handleOnDragEnd = async (event, fieldStaffId) => {
		const stageId = event.over?.id;
		const taskId = event.active?.id;
		const prevStageId = event.active.data.current?.stageId;
		const draggedFieldStaffId = event.active.data.current?.fieldStaffId;

		if (draggedFieldStaffId !== fieldStaffId) return;

		if (!stageId || !taskId || prevStageId === stageId) return;

		const task = tasks.find((t) => t.id === taskId);
		if (!task) return;
		if (stageId === "unAssigned") {
			try {
				await editTask({
					id: String(taskId),
					payload: { status: "unAssigned" },
				}).unwrap();
				setTasks((prev) =>
					prev.map((t) => (t.id === taskId ? { ...t, status: stageId } : t))
				);
				toast.success(trans(`task status is ${stageId}`), {
					autoClose: 800,
					rtl: localStorage.getItem("lang") === "ar" ? true : false,
				});
			} catch (error) {
				toast.error(trans(error?.data?.message), {
					autoClose: 800,
					rtl: localStorage.getItem("lang") === "ar" ? true : false,
				});
			}
			return;
		}
		if (prevStageId === "unAssigned") {
		}
		if (
			(prevStageId === "inProgress" && stageId === "todo") ||
			(prevStageId === "done" && stageId === "todo")
		) {
			setRescheduleTask({
				...task,
				newStatus: stageId,
				startTime: new Date(task.startTime) > new Date() ? task.startTime : "", // blank if past
				duration: task.expectedDuration || "",
			});
			return;
		}
		const payload = { status: stageId };
		const id = String(taskId);
		try {
			await editTask({ id, payload }).unwrap();
			setTasks((prev) =>
				prev.map((t) => (t.id === taskId ? { ...t, status: stageId } : t))
			);
			toast.success(trans(`task status is ${stageId}`), {
				autoClose: 800,
				rtl: localStorage.getItem("lang") === "ar" ? true : false,
			});
		} catch (error) {
			toast.error(trans(error?.data?.message), {
				autoClose: 800,
				rtl: localStorage.getItem("lang") === "ar" ? true : false,
			});
		}
	};
	return (
		<>
			<div className="space-y-10">
				{Object.values(tasksByFieldStaff).map(({ assignedTo, tasks }) => (
					<div key={assignedTo?.id || "unassigned"}>
						<KanbanBoardContainer
							title={
								<>
									<p>
										<FaUser color="#0f6936" size={16} />{" "}
										{assignedTo?.name || "Unassigned"}{" "}
										<Tag
											color={colors[Math.floor(Math.random() * colors.length)]}
										>
											{tasks.length} Tasks
										</Tag>
									</p>
								</>
							}
						>
							<KanbanBoard
								onDragEnd={(event) =>
									handleOnDragEnd(event, assignedTo?.id || "unassigned")
								}
								viewMode={viewMode}
							>
								{columns.map((col) => (
									<KanbanColumn
										id={col.id}
										title={col.title}
										count={tasks.filter((t) => t.status === col.id).length}
										key={col.id}
										viewMode={viewMode}
									>
										{tasks
											.filter((t) => t.status === col.id)
											.map((task) => (
												<KanbanItem
													key={task.id}
													id={task.id}
													data={{
														...task,
														stageId: col.id,
														fieldStaffId: assignedTo?.id || "unassigned",
													}}
													disabled={isDropdownOpen}
													onClick={() => setSelectedTask(task)}
												>
													<KanbanCardMemo
														{...task}
														users={users}
														canEdit={edit}
														canDelete={canDelete}
														isDropdownOpen={isDropdownOpen}
														setIsDropdownOpen={setIsDropdownOpen}
														onView={() => setSelectedTask(task)}
														onDelete={() => setDeleteTask(task)}
														onEdit={() =>
															handleShow("editForm", "task", task.id)
														}
													/>
												</KanbanItem>
											))}
									</KanbanColumn>
								))}
							</KanbanBoard>
						</KanbanBoardContainer>
					</div>
				))}
			</div>

			<TaskPopup
				taskId={selectedTask.id}
				task={selectedTask}
				onClose={() => setSelectedTask("")}
			/>
			<Delete taskId={deleteTask.id} />
		</>
	);
};

export default TasksBoardGroupByFieldStaff;

const PageSkeleton = () => {
	const columnCount = 6;
	const cardCount = 4;
	return (
		<KanbanBoardContainer>
			{Array.from({ length: columnCount }).map((_, index) => (
				<KanbanColumnSkeleton key={index}>
					{Array.from({ length: cardCount }).map((_, index) => (
						<KanbanCardSkeleton key={index} />
					))}
				</KanbanColumnSkeleton>
			))}
		</KanbanBoardContainer>
	);
};
